import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import itemGroupsActions from "../../../store/actions/items/categories/itemGroups/itemGroups.actions";
import AlertService from "../../../services/alerts/alert.service";
import { queryClient } from "../../../middlewares/cacheing/QueryClient.middleware";

import "../../../styles/components/dialogs/itemGroups/updateItemGroupImageDialogComponent.scss";

// 0036: upload / replace / remove the image of an item group.
//
// Item groups come from Rivhit, which does not populate `picture_link` — every group tile in the
// gallery and the header mega-menu renders the empty state. The uploaded image is stored locally
// (FilesDetails) and takes precedence over the Rivhit value at the DTO boundary, so removing it
// simply reverts the group to whatever Rivhit sends.
//
// Opened as ?d=itemGroup&action=image&id=<itemGroup guid>. The group itself is read from the
// itemGroups list already in Redux — the catalog-management screen loaded it before opening this.

const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const UpdateItemGroupImageDialogComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const itemGroupId = searchParams.get("id");
  const dispatch = useDispatch();

  const itemGroups = useSelector((state) => state.itemGroupsReducer?.itemGroups?.items || []);
  const itemGroup = useMemo(() => itemGroups.find((group) => group.id === itemGroupId), [itemGroups, itemGroupId]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Release the object URL when the preview is replaced or the dialog unmounts, otherwise the
  // blob stays alive for the lifetime of the tab.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const closeDialog = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("d");
    newSearchParams.delete("action");
    newSearchParams.delete("id");
    setSearchParams(newSearchParams);
  };

  // The upload changes what every other consumer of the list renders, so refresh the Redux list
  // (the catalog table) and drop the cached React Query lists behind the mega-menu and gallery.
  const refreshItemGroups = async () => {
    await dispatch(itemGroupsActions.getItemGroups({ pageNumber: 1, pageSize: 1000 }));
    queryClient.invalidateQueries({ queryKey: ["itemGroups"] });
    queryClient.invalidateQueries({ queryKey: ["categories"] });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      AlertService.error("קובץ לא נתמך", "ניתן להעלות קבצי תמונה בלבד");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      AlertService.error("הקובץ גדול מדי", "גודל התמונה המרבי הוא 5MB");
      event.target.value = "";
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      AlertService.warning("לא נבחרה תמונה", "יש לבחור תמונה לפני ההעלאה");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setIsSaving(true);
      await dispatch(itemGroupsActions.updateItemGroupImage(itemGroupId, formData));
      await refreshItemGroups();
      await AlertService.success("התמונה עודכנה", `התמונה של "${itemGroup?.itemGroupName}" נשמרה`);
      closeDialog();
    } catch (error) {
      console.error("Error updating item group image:", error);
      AlertService.error("שמירת התמונה נכשלה", error?.error?.message || "אירעה שגיאה בעת שמירת התמונה");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    // AlertService.confirm resolves to a plain boolean, not the SweetAlert result object.
    const confirmed = await AlertService.confirm("להסיר את התמונה?", "הקבוצה תחזור להצגה ללא תמונה", "הסר", "ביטול");
    if (!confirmed) return;

    try {
      setIsSaving(true);
      await dispatch(itemGroupsActions.deleteItemGroupImage(itemGroupId));
      await refreshItemGroups();
      await AlertService.success("התמונה הוסרה", "");
      closeDialog();
    } catch (error) {
      console.error("Error deleting item group image:", error);
      AlertService.error("הסרת התמונה נכשלה", error?.error?.message || "אירעה שגיאה בעת הסרת התמונה");
    } finally {
      setIsSaving(false);
    }
  };

  if (!itemGroup) {
    return (
      <div className="updateItemGroupImageContainer">
        <p className="notFoundMessage">קבוצת הפריטים לא נמצאה</p>
      </div>
    );
  }

  const currentImageUrl = itemGroup.pictureUrl;
  const displayedImageUrl = previewUrl || currentImageUrl;

  return (
    <div className="updateItemGroupImageContainer">
      <div className="dialogHeader">
        <h2>תמונת קבוצת פריטים</h2>
        <p className="groupName">
          {itemGroup.itemGroupName} <span className="groupCode">קוד {itemGroup.itemGroupId}</span>
        </p>
      </div>

      <div className="imagePreview">
        {displayedImageUrl ? (
          <img src={displayedImageUrl} alt={itemGroup.itemGroupName} />
        ) : (
          <div className="emptyImage">
            <i className="fa-solid fa-image"></i>
            <span>אין תמונה לקבוצה זו</span>
          </div>
        )}
        {previewUrl && <span className="previewBadge">תצוגה מקדימה — טרם נשמר</span>}
      </div>

      <label className="filePicker">
        <i className="fa-solid fa-upload"></i>
        <span>{selectedFile ? selectedFile.name : "בחר תמונה"}</span>
        <input type="file" accept="image/*" onChange={handleFileChange} disabled={isSaving} />
      </label>

      <p className="fileHint">קבצי תמונה בלבד, עד 5MB</p>

      <div className="dialogActions">
        <button type="button" className="primaryButton" onClick={handleUpload} disabled={isSaving || !selectedFile}>
          {isSaving ? (
            <>
              <i className="fa-solid fa-spinner fa-spin"></i> שומר...
            </>
          ) : (
            "שמור תמונה"
          )}
        </button>

        {currentImageUrl && (
          <button type="button" className="removeButton" onClick={handleRemove} disabled={isSaving}>
            <i className="fa-solid fa-trash"></i> הסר תמונה
          </button>
        )}
      </div>
    </div>
  );
};

export default UpdateItemGroupImageDialogComponent;
